import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import cn from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMusic } from "@fortawesome/free-solid-svg-icons/faMusic";
import { faLineChart, faPlus, faRecordVinyl } from "@fortawesome/free-solid-svg-icons";
import { faBars } from "@fortawesome/free-solid-svg-icons/faBars";
import { faBarsStaggered } from "@fortawesome/free-solid-svg-icons/faBarsStaggered";
import { useTranslation } from "../hooks/useTranslation";
import { faEdit } from "@fortawesome/free-solid-svg-icons/faEdit";
import { Button } from "./Button";
import { Link } from "./Link";
import { faScaleUnbalanced } from "@fortawesome/free-solid-svg-icons/faScaleUnbalanced";
import { useAuth } from "../contexts/AuthContext";
import { User } from "../api/models/User";
import { UploadLyricsButton } from "./UploadLyricsButton";
import { faQuestionCircle } from "@fortawesome/free-regular-svg-icons";
import { faUserPlus } from "@fortawesome/free-solid-svg-icons/faUserPlus";
import { CreateArtistModal } from "./Modals/CreateArtistModal";
import { faHome } from "@fortawesome/free-solid-svg-icons/faHome";

type AppRoute = {
  title: string;
  to: string;
  icon?: any;
  children?: Array<AppRoute>;
  isPlainText?: boolean;
};

type Props = {
  isExpanded: boolean;
  setIsExpanded: (value: boolean) => void;
};

function AppRoutes(t: any, user: User | null): AppRoute[] {
  const routes = [
    {
      title: `${t("albums")}`,
      to: "/music/albums",
      icon: faMusic
    },
    {
      title: `${t("tracks")}`,
      to: "/music/tracks",
      icon: faRecordVinyl
    },
    {
      title: `${t("drafts")}`,
      to: "/music/drafts",
      icon: faEdit
    },
    {
      title: `${t("statistics")}`,
      to: "/statistics",
      icon: faLineChart
    },
    {
      title: `${t("rights")}`,
      to: "/rights",
      icon: faScaleUnbalanced
    }
  ];

  if (user?.is_uma_employee) {
    routes.unshift({
      title: `${t("dashboard.dashboard")}`,
      to: "/dashboard",
      icon: faHome
    });
  }

  return routes;
}

export const Sidebar: React.FC<Props> = ({ isExpanded, setIsExpanded }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [visibleCreateArtistModal, setVisibleCreateArtistModal] = useState<boolean>(false);
  return (
    <div className={"text-white"}>
      <div className={"flex fixed h-full"}>
        {isExpanded ? (
          <div className={"flex flex-col h-screen w-56 border-none bg-neutral-800"}>
            <div className={"flex flex-col h-full justify-between"}>
              <div>
                <div className={"flex pt-3 px-4 justify-between align-content-between"}>
                  <div className={"text-xl"}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      preserveAspectRatio="xMidYMid"
                      width="90"
                      height="40"
                      viewBox="0 0 547 245"
                    >
                      <defs>
                        <style>
                          {`.cls-1 {
                      fill: #fff;
                      fill-rule: evenodd;
                    }`}
                        </style>
                      </defs>
                      <path
                        d="M542.396,149.741 L534.921,133.847 L504.943,133.847 L497.468,149.741 L493.298,149.741 L493.298,149.269 L519.106,94.506 L520.916,94.506 L546.488,149.269 L546.488,149.741 L542.396,149.741 ZM519.972,101.037 L506.281,130.385 L533.584,130.385 L519.972,101.037 ZM514.464,55.320 L495.502,55.320 L495.502,0.242 L514.464,0.242 C530.909,0.242 541.137,12.674 541.137,27.781 C541.137,43.124 530.830,55.320 514.464,55.320 ZM514.385,3.940 L499.514,3.940 L499.514,51.622 L514.307,51.622 C528.233,51.622 537.203,41.236 537.203,27.860 C537.203,14.562 528.312,3.940 514.385,3.940 ZM428.916,231.573 C434.896,231.573 439.617,228.819 441.112,222.839 L454.960,222.839 C452.836,238.497 440.640,244.713 428.916,244.713 C414.832,244.713 400.748,235.271 400.748,216.702 C400.748,197.740 414.832,188.534 428.916,188.534 C440.246,188.534 452.678,194.671 454.803,209.385 L440.955,209.385 C438.987,203.720 434.817,201.517 428.916,201.517 C420.576,201.517 415.147,207.496 415.147,216.624 C415.147,224.570 420.497,231.573 428.916,231.573 ZM401.001,0.242 L435.385,0.242 L435.385,12.753 L415.400,12.753 L415.400,20.621 L433.812,20.621 L433.812,33.210 L415.400,33.210 L415.400,42.180 L436.094,42.180 L436.094,55.320 L401.001,55.320 L401.001,0.242 ZM392.458,106.308 L392.458,94.663 L419.604,94.663 L419.604,106.308 L413.230,106.308 L413.230,138.096 L420.233,138.096 L420.233,149.741 L391.829,149.741 L391.829,138.096 L398.832,138.096 L398.832,106.308 L392.458,106.308 ZM304.280,197.661 L304.673,216.466 L304.673,244.162 L300.660,244.162 L300.660,189.006 L302.234,189.006 L339.923,235.350 L339.529,215.365 L339.529,189.006 L343.542,189.006 L343.542,244.162 L342.205,244.162 L304.280,197.661 ZM322.802,55.320 L318.868,55.320 L318.868,3.862 L300.771,3.862 L300.771,0.242 L340.899,0.242 L340.899,3.862 L322.802,3.862 L322.802,55.320 ZM281.532,149.741 L262.570,149.741 L262.570,94.663 L281.532,94.663 C297.977,94.663 308.205,107.095 308.205,122.202 C308.205,137.545 297.898,149.741 281.532,149.741 ZM281.453,98.362 L266.582,98.362 L266.582,146.043 L281.375,146.043 C295.301,146.043 304.271,135.657 304.271,122.281 C304.271,108.984 295.380,98.362 281.453,98.362 ZM219.569,43.675 L226.571,43.675 L226.571,11.887 L220.198,11.887 L220.198,0.242 L247.344,0.242 L247.344,11.887 L240.970,11.887 L240.970,43.675 L247.973,43.675 L247.973,55.320 L219.569,55.320 L219.569,43.675 ZM246.351,201.595 L226.365,201.595 L226.365,209.463 L244.777,209.463 L244.777,222.053 L226.365,222.053 L226.365,231.022 L247.059,231.022 L247.059,244.162 L211.966,244.162 L211.966,189.085 L246.351,189.085 L246.351,201.595 ZM147.566,94.663 L181.950,94.663 L181.950,107.174 L161.965,107.174 L161.965,115.042 L180.377,115.042 L180.377,127.631 L161.965,127.631 L161.965,136.601 L182.658,136.601 L182.658,149.741 L147.566,149.741 L147.566,94.663 ZM115.955,8.819 L116.348,27.624 L116.348,55.320 L112.335,55.320 L112.335,0.164 L113.909,0.164 L151.598,46.507 L151.204,26.522 L151.204,0.164 L155.217,0.164 L155.217,55.320 L153.879,55.320 L115.955,8.819 ZM129.740,188.298 C140.834,188.298 150.040,192.940 153.974,203.956 L149.962,203.956 C146.264,195.379 139.103,191.996 129.662,191.996 C115.577,192.232 106.450,202.618 106.450,216.466 C106.450,230.550 115.341,241.172 129.662,241.172 C142.802,241.172 151.614,232.360 152.479,219.377 L130.763,219.377 L130.763,215.915 L156.414,215.915 C156.414,220.951 155.784,225.593 153.974,229.764 C149.883,239.441 141.071,244.949 129.662,244.949 C113.689,244.949 102.516,233.068 102.516,216.466 C102.516,200.022 114.082,188.298 129.740,188.298 ZM54.343,104.499 L34.200,133.611 L33.413,133.611 L13.192,104.499 L13.192,149.741 L9.179,149.741 L9.179,94.585 L10.832,94.585 L33.807,127.631 L56.703,94.585 L58.356,94.585 L58.356,149.741 L54.343,149.741 L54.343,104.499 ZM29.243,56.028 C17.598,56.028 6.819,49.497 6.819,37.144 L6.819,0.242 L21.218,0.242 L21.218,36.830 C21.218,41.393 25.230,43.360 29.322,43.360 C33.177,43.360 37.269,41.393 37.269,36.830 L37.269,0.242 L51.510,0.242 L51.510,37.144 C51.510,49.497 40.810,56.028 29.243,56.028 ZM32.312,188.534 L57.333,242.195 L57.333,244.162 L42.462,244.162 L39.236,237.317 L18.621,237.317 L15.474,244.162 L0.446,244.162 L0.446,242.195 L25.703,188.534 L32.312,188.534 ZM34.672,225.279 L28.929,211.981 L23.263,225.279 L34.672,225.279 ZM523.748,217.017 L523.905,217.017 L541.845,189.085 L546.094,189.085 L546.094,189.636 L525.794,221.266 L525.794,244.162 L521.860,244.162 L521.860,221.266 L501.481,189.557 L501.481,189.085 L505.808,189.085 L523.748,217.017 Z"
                        className="cls-1"
                      />
                    </svg>
                  </div>

                  <FontAwesomeIcon
                    className={"flex cursor-pointer ml-4"}
                    icon={faBars}
                    size={"2x"}
                    onClick={() => {
                      setIsExpanded(!isExpanded);
                    }}
                  />
                </div>
                <div className={"text-center my-4"}>
                  <Link to={"/music/drafts/create"}>
                    <Button
                      label={t("new_release_button").toString()}
                      className={
                        "bg-blue-500 w-5/6 text-white hover:bg-blue-400 border-none"
                      }
                      icon={faPlus}
                    />
                  </Link>
                </div>
                <div className={"flex w-56 flex-col border-none"}>
                  <nav>
                    {AppRoutes(t, user).map((route) => {
                      return (
                        <div key={route.to}>
                          {route.isPlainText ? (
                            <div
                              className={
                                "px-4 py-2 text-gray-400 transform text-md"
                              }
                            >
                              {route.title.toUpperCase()}
                            </div>
                          ) : (
                            <NavLink
                              className={({ isActive }) =>
                                twMerge(
                                  cn(
                                    "flex items-center text-white px-4 py-3 transition-colors duration-300 transform hover:bg-neutral-700 hover:text-gray-700",
                                    {
                                      "bg-neutral-700": isActive
                                    }
                                  )
                                )
                              }
                              style={({ isActive }) => {
                                return {
                                  textDecoration: "none",
                                  borderRight: isActive
                                    ? "4px solid #0d6efd"
                                    : "none",
                                  marginTop: 0
                                };
                              }}
                              to={route.to}
                            >
                              {route?.icon && <FontAwesomeIcon icon={route.icon} />}
                              <span className="mx-2 text-sm font-medium">
                            {route.title}
                          </span>
                            </NavLink>
                          )}

                          {route?.children?.map((childrenRoute) => {
                            return (
                              <NavLink
                                className={({ isActive }) =>
                                  twMerge(
                                    cn(
                                      "flex items-center px-4 py-3 text-white transition-colors duration-300 transform hover:bg-neutral-700 hover:text-gray-700",
                                      {
                                        "bg-neutral-700": isActive
                                      }
                                    )
                                  )
                                }
                                style={({ isActive }) => {
                                  return {
                                    textDecoration: "none",
                                    borderRight: isActive
                                      ? "4px solid #0d6efd"
                                      : "none",
                                    marginTop: 0
                                  };
                                }}
                                to={childrenRoute.to}
                              >
                                {childrenRoute?.icon && (
                                  <FontAwesomeIcon
                                    icon={childrenRoute.icon}
                                    size={"xs"}
                                  />
                                )}
                                <span className="mx-2 text-sm font-medium">
                              {childrenRoute.title}
                            </span>
                              </NavLink>
                            );
                          })}
                        </div>
                      );
                    })}
                  </nav>
                </div>
                <div className={"flex justify-center mt-4"}>
                  <UploadLyricsButton />
                </div>
                <div className={"flex justify-center items-center mt-4 w-full"}>
                  <Button
                    label={"Создать артиста"}
                    onClick={() => setVisibleCreateArtistModal(true)}
                    icon={faUserPlus}
                    className={"w-5/6 text-white border hover:bg-gray-700 rounded-lg"}
                  />
                </div>
              </div>
            </div>

            <div className={"flex mt-4"}>
              <Button
                icon={faQuestionCircle}
                label={t("help").toString()}
                onClick={() => navigate("/help")}
                className={"bg-none w-full text-center text-white hover:bg-opacity-0 cursor-pointer border-none rounded-lg"}
              />
            </div>
          </div>
        ) : (
          <div className={"flex flex-col justify-between h-full fixed border-none bg-neutral-800"}>
            <div>
              <div
                className={"flex pt-3 items-center justify-center cursor-pointer"}
                onClick={() => {
                  setIsExpanded(!isExpanded);
                }}
              >
                <FontAwesomeIcon
                  className={"flex"}
                  icon={isExpanded ? faBarsStaggered : faBars}
                  size={"2x"}
                />
              </div>

              <Link to={"/music/drafts/create"}>
                <button className={"bg-blue-500 ml-3.5 mt-4 w-9 h-9 rounded-lg border-none"}>
                  <FontAwesomeIcon className={"text-white"} icon={faPlus} />
                </button>
              </Link>

              <div className={"flex w-16 flex-col mt-3 items-center"}>
                <nav className="w-full">
                  {AppRoutes(t, user).map((route) => {
                    return (
                      <div className="">
                        {route.isPlainText ? (
                          <div className={"px-1 py-2 ml-5 text-gray-400 text-ml"}>
                            {route.title[0].toUpperCase()}
                          </div>
                        ) : (
                          <NavLink
                            className={({ isActive }) =>
                              twMerge(
                                cn(
                                  "flex items-center px-4 py-4 text-white hover:bg-neutral-700 hover:text-white transition-colors duration-300 transform",
                                  {
                                    "bg-neutral-700": isActive
                                  }
                                )
                              )
                            }
                            style={({ isActive }) => {
                              return {
                                textDecoration: "none",
                                borderRight: isActive
                                  ? "4px solid #0d6efd"
                                  : "none",
                                marginTop: 0
                              };
                            }}
                            to={route.to}
                          >
                            {route?.icon && (
                              <FontAwesomeIcon
                                className={"text-white"}
                                icon={route.icon}
                              />
                            )}
                          </NavLink>
                        )}
                        {route?.children?.map((childrenRoute) => {
                          return (
                            <NavLink
                              className={({ isActive }) =>
                                twMerge(
                                  cn(
                                    "flex items-center px-4 py-4 text-white hover:bg-neutral-700 hover:text-white transition-colors duration-300 transform",
                                    {
                                      "bg-neutral-700": isActive
                                    }
                                  )
                                )
                              }
                              style={({ isActive }) => {
                                return {
                                  textDecoration: "none",
                                  borderRight: isActive
                                    ? "4px solid #0d6efd"
                                    : "none",
                                  marginTop: 0
                                };
                              }}
                              to={childrenRoute.to}
                            >
                              {childrenRoute?.icon && (
                                <FontAwesomeIcon
                                  icon={childrenRoute.icon}
                                  className={"dark:text-white"}
                                  size={"sm"}
                                />
                              )}
                            </NavLink>
                          );
                        })}
                      </div>
                    );
                  })}
                </nav>
              </div>

              <div className={"flex justify-center mt-3"}>
                <UploadLyricsButton isCompact />
              </div>
              <div className={"flex justify-center items-center mt-4 w-full"}>
                <Button
                  onClick={() => setVisibleCreateArtistModal(true)}
                  icon={faUserPlus}
                  className={"bg-blue-500 px-0 py-0 w-9 h-9 text-white hover:bg-blue-400 rounded-3xl border-none"}
                />
              </div>
            </div>
            <div className={"flex justify-center mb-3"}>
              <Button
                icon={faQuestionCircle}
                onClick={() => navigate("/help")}
                className={"bg-none px-0 text-xl py-0 w-9 h-9 text-white hover:bg-opacity-0 rounded-3xl border-none"}
              />
            </div>
          </div>
        )}
      </div>
      {
        visibleCreateArtistModal &&
        <CreateArtistModal onClose={() => setVisibleCreateArtistModal(false)} />
      }
    </div>
  );

};
